import React, { FC, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type ResultsTableProps = {
    values: string[][];
};

type Column = {
    label: string;
    index: number;
    group?: 'scratch' | 'tempo' | 'elimination' | 'points';
};

export const OmniumTable: FC<ResultsTableProps> = ({ values }) => {
    const [headers, ...rows] = values;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const isAbandoned = (value?: string) => value === 'ABD' || value === 'DNF' || value === 'DNS';

    const isQualified = (value?: string) => value === 'Q';

    // ✅ Build column config dynamically
    const columns: Column[] = useMemo(() => {
        return headers.map((h, i) => {
            if (i >= 5 && i <= 6) return { label: h, index: i, group: 'scratch' };
            if (i >= 7 && i <= 8) return { label: h, index: i, group: 'tempo' };
            if (i >= 9 && i <= 10) return { label: h, index: i, group: 'elimination' };
            if (i === 11) return { label: h, index: i, group: 'points' };
            return { label: h, index: i };
        });
    }, [headers]);

    // ✅ Detect which groups actually exist
    const groups = {
        scratch: columns.some((c) => c.group === 'scratch'),
        tempo: columns.some((c) => c.group === 'tempo'),
        elimination: columns.some((c) => c.group === 'elimination'),
        points: columns.some((c) => c.group === 'points'),
    };

    const countryColIndex = headers.findIndex((h) => h.toLowerCase() === 'country');
    const qColIndex = headers.findIndex((h) => h.toLowerCase() === 'q');

    // =========================================================
    // 📱 MOBILE VIEW
    // =========================================================
    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2, marginBottom: 2 }}>
                {rows.map((row, rowIndex) => {
                    const rank = row[0];
                    const firstName = row[3];
                    const lastName = row[2];
                    const country = row[countryColIndex];
                    const totalPoints = row[row.length - 1];

                    const abandoned = isAbandoned(rank);

                    // ✅ Extract grouped values dynamically
                    const getPair = (startIndex?: number) =>
                        startIndex !== undefined && row[startIndex] && row[startIndex + 1]
                            ? `${row[startIndex]} | ${row[startIndex + 1]}`
                            : undefined;

                    const scratch = groups.scratch ? getPair(5) : undefined;
                    const tempo = groups.tempo ? getPair(7) : undefined;
                    const elimination = groups.elimination ? getPair(9) : undefined;
                    const pointsRace = groups.points ? row[11] : undefined;

                    const stats = [
                        scratch && { label: 'Scratch', value: scratch },
                        tempo && { label: 'Tempo', value: tempo },
                        elimination && { label: 'Elim', value: elimination },
                        pointsRace && { label: 'Points', value: pointsRace },
                    ].filter(Boolean) as { label: string; value: string }[];

                    return (
                        <Paper
                            key={rowIndex}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {/* Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography fontWeight={700}>
                                    {rank === 'DNF' ? '' : '# '}
                                    {rank}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                    }}
                                >
                                    {firstName} {lastName}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                    }}
                                >
                                    {totalPoints} pts
                                </Typography>
                            </Box>

                            {/* Stats */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${stats.length + 1}, 1fr)`,
                                    gap: 1,
                                    mt: 1,
                                }}
                            >
                                {stats.map((s, i) => (
                                    <Stat key={i} label={s.label} value={s.value} />
                                ))}

                                {/* Flag */}
                                <Box sx={{ textAlign: 'center' }}>
                                    <img
                                        src={getFlagUrl(country)}
                                        alt={country}
                                        style={{ marginTop: '1rem', marginLeft: '1rem' }}
                                        className="flag-icon"
                                    />
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    // =========================================================
    // 🖥️ DESKTOP VIEW
    // =========================================================
    const headerStyles = {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        backgroundColor: 'grey.900',
        color: 'common.white',
        whiteSpace: 'nowrap',
    };

    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, marginTop: 2, marginBottom: 2 }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={5} sx={headerStyles} />

                        {groups.scratch && (
                            <TableCell colSpan={2} align="center" sx={headerStyles}>
                                Scratch
                            </TableCell>
                        )}

                        {groups.tempo && (
                            <TableCell colSpan={2} align="center" sx={headerStyles}>
                                Tempo Race
                            </TableCell>
                        )}

                        {groups.elimination && (
                            <TableCell colSpan={2} align="center" sx={headerStyles}>
                                Elimination
                            </TableCell>
                        )}

                        {groups.points && (
                            <TableCell colSpan={1} align="center" sx={headerStyles}>
                                Points Race
                            </TableCell>
                        )}

                        <TableCell sx={headerStyles} />
                    </TableRow>

                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.index} align="center" sx={headerStyles}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex} hover>
                            {columns.map((col) => {
                                const cellValue = row[col.index];
                                const abandoned = isAbandoned(cellValue);
                                const qualified = col.index === qColIndex && isQualified(cellValue);

                                if (qualified) {
                                    return (
                                        <TableCell key={col.index} align="center">
                                            <span className="qualified-badge">Q</span>
                                        </TableCell>
                                    );
                                }

                                if (col.index === countryColIndex && cellValue) {
                                    return (
                                        <TableCell key={col.index} align="center">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <img
                                                    src={getFlagUrl(cellValue)}
                                                    alt={cellValue}
                                                    className="flag-icon"
                                                />
                                            </Box>
                                        </TableCell>
                                    );
                                }

                                return (
                                    <TableCell
                                        key={col.index}
                                        align="center"
                                        sx={{
                                            fontWeight: abandoned || col.index === 0 ? 600 : 400,
                                        }}
                                    >
                                        {cellValue ?? '—'}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const Stat = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography fontWeight={600}>{value ?? '—'}</Typography>
    </Box>
);
